import { FEELINGS, FEELING_REASONS, FATIGUE_OPTIONS, CLOSURE_OPTIONS } from './constants.js';
import { surveyDateAtNoon, toSurveyDateKey } from './surveyDate.js';

const ARRIVAL_REASON_LABELS = {
    ...FEELING_REASONS,
    no_dormi: { emoji: '😴', label: 'Descanso o sueño' },
    problemas_personales: { emoji: '🏠', label: 'Algo personal o familiar' },
    el_trabajo: { emoji: '⚙️', label: 'La carga del trabajo' },
    el_jefe: { emoji: '👤', label: 'Relación con la persona guía' },
};

/** Agrupa registros daily_surveys por fecha (llegada + cierre). */
export function buildWellbeingDays(dailySurveys = []) {
    const byDate = new Map();

    for (const row of dailySurveys) {
        const dateIso = toSurveyDateKey(row.survey_date);
        if (!dateIso) continue;
        if (!byDate.has(dateIso)) {
            byDate.set(dateIso, { dateIso, start: null, end: null });
        }
        const day = byDate.get(dateIso);
        if (row.survey_type === 'end') day.end = row;
        else day.start = row;
    }

    return Array.from(byDate.values()).sort((a, b) => String(b.dateIso).localeCompare(String(a.dateIso)));
}

function reasonLabel(key) {
    if (!key) return null;
    return ARRIVAL_REASON_LABELS[key] || { emoji: '·', label: key };
}

/** Resumen legible de un día para supervisor / ficha completa. */
export function getWellbeingDaySummary(day) {
    if (!day) return null;

    const d = surveyDateAtNoon(day.dateIso);
    if (!d) return null;
    const dateLong = d.toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    const dateShort = d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });

    const start = day.start;
    const end = day.end;
    const feeling = start?.feeling;
    const mood = FEELINGS[feeling];
    const reason = reasonLabel(start?.feeling_reason);

    const lines = [];

    if (start) {
        lines.push({
            label: 'Llegada a jornada',
            value: mood ? `${mood.emoji} ${mood.label}` : feeling || '—',
        });
        if (reason) {
            lines.push({ label: 'Motivo', value: `${reason.emoji} ${reason.label}` });
        }
    } else {
        lines.push({ label: 'Llegada', value: 'Sin registro de llegada ese día' });
    }

    if (end) {
        const fatigue = FATIGUE_OPTIONS[end.fatigue];
        const closure = CLOSURE_OPTIONS[end.closure];
        lines.push({
            label: 'Cierre de jornada',
            value: fatigue ? `${fatigue.emoji} ${fatigue.label}` : '—',
        });
        if (closure) {
            lines.push({ label: 'Cierre', value: `${closure.emoji} ${closure.label}` });
        }
    } else if (start) {
        lines.push({ label: 'Cierre', value: 'Sin cierre registrado ese día' });
    }

    return {
        dateIso: day.dateIso,
        dateLong,
        dateShort,
        emoji: mood?.emoji || '·',
        feelingLabel: mood?.label || 'Sin dato',
        lines,
        hasStart: !!start,
        hasEnd: !!end,
    };
}
